import React, { useEffect, useState } from 'react';

import { useAuth } from '../../../hooks/auth';
import { useTheme } from '../../../hooks/theme';
import { useHistory } from 'react-router-dom';

import api from '../../../services/api';

import { Atom, Chats, Exam, Play } from 'phosphor-react';

import CardRanking from '../../../components/CardRanking';
import VideoTumbnail from '../../../components/VideoTumbnail';
import ShowByBreakpoint from '../../../utils/breakpointSize';
import TodoListModule from '../../../components/TodoListModule';
import TooltipAdapter from '../../../components/TooltipAdapter';
import ModalDashboardBanner from '../../../components/ModalDashboardBanner';
import CardMainFeatures from '../../../components/CardMainFeatures';
import { IPraticeProps } from '../../../components/SimulatedModule';
import CardSimulatesProgress from '../../../components/CardSimulatesProgress';

import {
  Container,
  SectionContainer1,
  SectionContainer2,
  SectionContainer4,
  CardExamNotFound,
  GraphicContainer,
  SectionContainer0,
} from './styles';

import { ILessonProps } from '../../../interfaces/Lesson';
import CardCalendar from '../../../components/CardCalendar';
import CardTimer from '../../../components/CardTimer';
import CardEstudometro from '../../../components/CardEstudometro';
import ReactApexChart, { Props } from 'react-apexcharts';
import { FiInfo } from 'react-icons/fi';
import CardLevelProgress from '../../../components/CardLevelProgress';
import { API_S3 } from '../../../services/api_s3';
import CardModule from '../../../components/CardModule';
import FakeHorizontalSubHeader from '../../../components/FakeHorizontalSubHeader';
import LoaderAtomic from '../../../components/LoaderAtomic';
import CardModuleHome from '../../../components/CardModuleHome';

interface IStatisticsProps {
  totalLessonViews: number;
  totalExercises: number;
  totalTopicAnswers: number;
  totalPractices: number;
}

interface IGraphPraticeStatisticsProps {
  categories: string[];
  series: {
    name: string;
    data: number[];
  }[];
}

const Dashboard: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const history = useHistory();

  // Loading
  const [dashboardLoading, setDashboardLoading] = useState(true);

  // Data
  const [lastVideoData, setLastVideoData] = useState<ILessonProps>(
    {} as ILessonProps,
  );
  const [userStatistics, setUserStatistics] = useState<IStatisticsProps>(
    {} as IStatisticsProps,
  );
  const [praticesStatistics, setPraticesStatistics] =
    useState<IGraphPraticeStatisticsProps>({} as IGraphPraticeStatisticsProps);
  const [lastPraticeData, setLastPraticeData] = useState<
    IPraticeProps | undefined
  >(undefined);

  const [welcomeData, setWelcomeData] = useState<number>(0);

  // Get last video viewed
  useEffect(() => {
    (async () => {
      try {
        setDashboardLoading(true);
        const [
          responseLastLessons,
          responseUserStatistics,
          responsePraticeStatistics,
          responseWelcomeStatistics,
        ] = await Promise.all([
          api.get('/clusters/lessons/last'),
          api.get('/statistics/profile-counters'),
          api.get('/practices/statistics'),
          api.get('/welcome'),
        ]);

        // Last Lesson
        console.log(responseLastLessons.data);
        setLastVideoData(responseLastLessons.data);

        // User Statistics
        console.log(responseUserStatistics.data);
        setUserStatistics(responseUserStatistics.data);

        // Practice Statistics
        console.log('Practice Statistics: ', responsePraticeStatistics.data);
        setPraticesStatistics(responsePraticeStatistics.data);

        const responseLastPratice = await api.get('/practices/last');

        // Last Pratice
        console.log(responseLastPratice.data);
        setLastPraticeData(responseLastPratice.data);

        // Welcome Statistics
        console.log(responseWelcomeStatistics.data);
        setWelcomeData(responseWelcomeStatistics.data);
      } catch (error) {
        console.log(error);
        setDashboardLoading(false);
      } finally {
        setDashboardLoading(false);
      }
    })();
  }, []);

  const lineChartOptionsDashboard: Props = {
    chart: {
      toolbar: {
        show: false,
      },
    },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: function (value: number) {
          return value.toFixed(0) + '%';
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
    },
    xaxis: {
      type: 'name',
      /* categories: [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez",
      ], */
      categories: praticesStatistics?.categories,
      labels: {
        style: {
          colors: theme === 'dark' ? '#dadbe790' : '#dadbe7',
          fontSize: '10px',
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: theme === 'dark' ? '#dadbe790' : '#dadbe7',
          fontSize: '10px',
        },
      },
    },
    legend: {
      show: false,
    },
    grid: {
      strokeDashArray: 5,
      borderColor: theme === 'dark' ? '#dadbe750' : '#dadbe7',
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'vertical',
        shadeIntensity: 0,
        gradientToColors: undefined, // optional, if not defined - uses the shades of same color in series
        inverseColors: true,
        opacityFrom: 0.8,
        opacityTo: 0,
        stops: [],
      },
      colors:
        theme === 'dark'
          ? ['#bcfed6', '#bcfed6'] // dark
          : ['#fe157e', '#fe157e'], // light
    },
    colors:
      theme === 'dark'
        ? ['#bcfed6', '#bcfed6'] // dark
        : ['#fe157e', '#fe157e'], // light
  };

  const greetingsMessages = {
    '-1': 'Você está há um dia sem estudar. Cuidado para não perder o ritmo, hein?! ⏳',
    '-2': 'Dois dias sem estudar? Seu conhecimento pode começar a evaporar. 📉',
    '-3': 'Três dias fora... Não deixe sua jornada ser interrompida. Seu futuro depende de sua constância! 🛑',
    '-4': 'Quatro dias sem realizar nenhuma atividade na plataforma. O tempo perdido é difícil de recuperar! ⚠️',
    '-5': 'Cinco dias fora! Seu progresso está em risco. Não deixe seu esforço ir por água abaixo. 🚨',
    '-6': 'Seis dias longe da plataforma. Estudar diariamente é o segredo do sucesso! 🔁',
    '-7': 'Uma semana sem estudar. Sua ausência está comprometendo seus resultados. ⏰',
    '-8': 'Oito dias sem acesso! A consistência é o que transforma estudo em conquistas. Não desista! 🛑',
    '-9': 'Nove dias sem estudar... O tempo perdido agora pode afetar seus sonhos no futuro. Reaja! ⚡',
    '-10': 'Dez dias longe! O sucesso exige compromisso diário. 🚨',
    '-11':
      'Onze dias fora da plataforma! Sua dedicação precisa ser renovada! 💥',
    '0': 'Seja bem-vindo à melhor plataforma de ensino de Química do Brasil!',
    '1': 'Bem-vindo de volta! Que tal continuar assim, estudando todo dia? 😊',
    '3': 'Você está indo muito bem! Continue assim! 😊',
    '4': 'Você está arrasando! Continue firme! 💪',
    '5': 'Olha só quem está criando um hábito de estudos! Continue assim! 😊',
    '6': 'Estudar é como química: cada dia adiciona uma nova molécula ao seu sucesso! 🌟',
    '7': 'Uma semana de estudos consecutivos! Você é pura inspiração! 🏅',
    '8': 'A fórmula do sucesso é a consistência! E você está no caminho certo! 🔥',
    '9': 'Já são 9 dias seguidos estudando! Você é o elemento mais estável dessa tabela periódica! 💎',
    '10': 'Dez dias incríveis! Sua dedicação é como uma reação química perfeita: explosiva e eficiente! 🚀',
    '11': 'Você está no topo da tabela periódica da dedicação! Continue brilhando! 🌟',
  };

  const checkIfDaysStudy = (days: number) => {
    if (days < -11) {
      return `Poxa, ${user.nickname} voce está há ${days * -1} dias sem estudar. Volte logo para não perder o ritmo! ⏳`;
    } else {
      return `Você está há ${days} dias estudando consecutivamente. Continue assim! 😊`;
    }
  };

  return (
    <>
      {theme === 'light' && <FakeHorizontalSubHeader />}
      {/* <ModalHomeAlert /> */}
      <ModalDashboardBanner
        imageUrl="https://kxqcnthcrvgpokjxoldz.supabase.co/storage/v1/object/public/cabral-api/site/modal-enem2026.png"
        redirectUrl="https://forms.gle/YiXFY4A2A6kzNVNy8"
        dismissKey="dashboard_banner_main"
      />
      <LoaderAtomic isVisible={dashboardLoading || !user.id} />

      <Container>
        <aside>
          <SectionContainer0
            style={
              theme === 'dark'
                ? {
                    backgroundImage: `url(${API_S3}/default/shadow-green.webp)`,
                  }
                : {}
            }
          >
            <aside>
              <img
                src={`${API_S3}/emojis-avatar/${welcomeData <= -11 ? '-11' : welcomeData || '0'}.png`}
                alt="Olá! Como você está?"
              />
            </aside>
            <main>
              {user.id && (
                <h1>
                  Fala,{' '}
                  {user?.nickname
                    ? user.nickname.split(' ')[0]
                    : user.first_name.split(' ')[0]}
                  !
                </h1>
              )}
              <p>
                {welcomeData in greetingsMessages
                  ? greetingsMessages[
                      String(welcomeData) as keyof typeof greetingsMessages
                    ]
                  : checkIfDaysStudy(Number(welcomeData))}
              </p>
            </main>
          </SectionContainer0>
          <SectionContainer1>
            <CardMainFeatures
              icon={Play}
              title="Aulas assistidas"
              value={userStatistics?.totalLessonViews || 0}
            />
            <CardMainFeatures
              icon={Atom}
              title="Exercícios resolvidos"
              value={userStatistics?.totalExercises || 0}
            />
            <CardMainFeatures
              icon={Chats}
              title="Tópicos respondidos"
              value={userStatistics?.totalTopicAnswers || 0}
            />
            <CardMainFeatures
              icon={Exam}
              title="Simulados realizados"
              value={userStatistics?.totalPractices || 0}
            />
          </SectionContainer1>

          <SectionContainer2>
            {lastVideoData && (
              <VideoTumbnail
                videoTitle={lastVideoData?.title}
                videoTumbnailUrl={
                  lastVideoData?.video_provider === 'panda'
                    ? `https://b-vz-cb1dc143-4cf.tv.pandavideo.com.br/${lastVideoData?.video}/thumbnail.jpg`
                    : lastVideoData?.video_provider === 'youtube'
                      ? `https://img.youtube.com/vi/${lastVideoData?.video}/maxresdefault.jpg`
                      : lastVideoData?.thumbnail_url
                }
                /* videoTumbnailUrl={'https://i.ytimg.com/vi_webp/k4Y3VfZHvLw/maxres2.webp'} */
                percentView={
                  (Number(lastVideoData?.viewed) * 100) /
                  Number(lastVideoData?.duration)
                }
                dateView={lastVideoData?.updated_at}
                onClick={() => {
                  lastVideoData.is_study_plan
                    ? history.push(
                        `/plano-de-estudos/${user.study_plan?.slug}/${lastVideoData.cluster?.slug}/${lastVideoData.slug}`,
                      )
                    : history.push(
                        `/aulas/${lastVideoData.cluster?.slug}/${lastVideoData.slug}`,
                      );
                }}
              />
            )}

            <ShowByBreakpoint size={1025}>
              {lastPraticeData ? (
                <CardSimulatesProgress
                  id={lastPraticeData?.id}
                  totalQuestions={lastPraticeData?.total_questions}
                  totalCorrects={lastPraticeData?.total_hits}
                  link={`/simulado/${lastPraticeData.id}`}
                  expires_at={lastPraticeData?.expires_at}
                  created_at={lastPraticeData?.created_at}
                  isAvailable={lastPraticeData?.is_available}
                  isFinished={lastPraticeData?.is_finished}
                  height="100%"
                />
              ) : (
                <CardExamNotFound to="/simulados/novo">
                  <header>
                    <TooltipAdapter
                      alt="Gere novos simulados quando quiser. 😊"
                      place="top"
                      cursor="help"
                    >
                      <Exam size={64} color="#ffffff" weight="duotone" />
                    </TooltipAdapter>
                  </header>
                  <footer>
                    <h2>Você ainda não fez nenhum simulado</h2>
                    <p>Que tal começar agora mesmo? 🤷‍♂️</p>
                  </footer>
                  <main />
                </CardExamNotFound>
              )}
            </ShowByBreakpoint>
          </SectionContainer2>

          {/*   <SectionContainer3>
            <CardLevelProgress />
          </SectionContainer3> */}

          <SectionContainer4>
            <CardRanking />
            <div>
              {user.study_plan?.id ? (
                <CardModuleHome
                  id={user.study_plan?.id}
                  title={user.study_plan?.title}
                  description={user.study_plan?.description}
                  link={`/plano-de-estudos/${user.study_plan?.slug}/!`}
                  checked={true}
                  background={user.study_plan.thumbnail_url}
                  bgColor={user.study_plan.background_color}
                />
              ) : (
                <CardModule
                  id={'77'}
                  title="Escolha seu plano de estudos"
                  description="Comece a estudar com um plano personalizado."
                  link={`/plano-de-estudos/?change=true`}
                  checked={false}
                  linkTitle="Escolher plano"
                  background={`${API_S3}/default/escolher-plano.webp`}
                  bgColor={theme === 'dark' ? '#00dfcb' : '#fe157e'}
                />
              )}
              <CardTimer />
              <CardLevelProgress />
              <GraphicContainer>
                <header>
                  <aside>
                    <h2>Simulados</h2>
                    <p>Acompanhe o seu desempenho nos simulados</p>
                  </aside>
                  <TooltipAdapter
                    alt="As estatísticas dos simulados <br /> são  atualizadas diariamente."
                    place="top"
                    cursor="help"
                  >
                    <FiInfo strokeWidth={2.5} size={20} />
                  </TooltipAdapter>
                </header>
                <main>
                  {praticesStatistics?.series && (
                    <ReactApexChart
                      options={lineChartOptionsDashboard}
                      //series={praticesStatistics?.series}
                      series={praticesStatistics?.series.map(serie => {
                        return {
                          name: serie.name,
                          data: serie.data.map(data => {
                            return Number((data * 100).toFixed(0));
                          }),
                        };
                      })}
                      type="area"
                      width="100%"
                      height="100%"
                    />
                  )}
                </main>
              </GraphicContainer>
            </div>
          </SectionContainer4>
        </aside>
        <main>
          <CardCalendar />
          <CardEstudometro />
          <TodoListModule />
        </main>
      </Container>
    </>
  );
};

export default Dashboard;
